<template>
  <!-- Nothing at all unless the server actually refused with an answerable
       ambiguity. `candidatesFromRefusal` returns null for every other refusal,
       and a "Which one?" block with nothing in it would be worse than the
       plain error line the dock already shows. -->
  <div v-if="model" class="sa-voice-candidates">
    <p class="sa-voice-candidates-q">{{ questionText }}</p>

    <!-- Both boxes in one puck: naming the puck cannot separate them, so no
         chip here could resolve anything. Say so instead of offering taps that
         would come back with the same refusal. -->
    <div
      v-if="tappable"
      class="sa-voice-candidates-chips"
      role="group"
      :aria-label="questionText"
    >
      <button
        v-for="(choice, i) in model.choices"
        :key="i + '|' + choice.value + '|' + choice.label"
        type="button"
        class="sa-voice-chip"
        @click="pick(choice)"
      >
        <span class="sa-voice-chip-label">{{ choice.label }}</span>
        <span v-if="choice.sublabel" class="sa-voice-chip-sub">{{
          choice.sublabel
        }}</span>
      </button>
    </div>

    <p class="sa-voice-candidates-help">{{ helpText }}</p>
  </div>
</template>

<script>
// Spelled out for 2..9 because the question is read aloud as well as shown,
// and "2 boxes are called sample alpha" reads as a count of something rather
// than as a sentence. Ten or more stays a numeral: nobody is tapping through
// ten identically named boxes, and the digits are easier to scan.
const COUNT_WORDS = [
  "",
  "",
  "Two",
  "Three",
  "Four",
  "Five",
  "Six",
  "Seven",
  "Eight",
  "Nine",
];

export default {
  name: "VoiceCandidates",
  props: {
    candidates: {
      type: Object,
      default: null,
    },
  },
  emits: ["choose"],
  computed: {
    model() {
      const c = this.candidates;
      return c && Array.isArray(c.choices) && c.choices.length ? c : null;
    },

    questionText() {
      const question = String(this.model?.question || "");
      return question.replace(
        /^([2-9])(?=\s)/,
        (match, digit) => COUNT_WORDS[Number(digit)]
      );
    },

    // Absent means "yes": only the box shape can be unresolvable, and only it
    // sets the flag.
    tappable() {
      return this.model?.resolvable !== false;
    },

    helpText() {
      if (this.tappable) return "Tap one, say the puck, or look at it.";
      return "Say which well, or use the screen.";
    },
  },
  methods: {
    // The parent needs the identifier the SERVER echoed to decide which field
    // was ambiguous, and that lives on the parent object, not on the choice.
    // Carrying it here keeps `fieldsWithChoice(intent, fields, choice, kind)`
    // callable with exactly what this emits.
    pick(choice) {
      this.$emit("choose", {
        ...choice,
        identifier: this.model.identifier,
      });
    },
  },
};
</script>

<style scoped>
/* Plain CSS, no Tailwind: public/assets/styles.css is a committed, pre-compiled
   artefact that the Vue build does not regenerate, so a utility class not
   already in it silently does nothing. Same reasoning as VoiceControl.vue. */

.sa-voice-candidates {
  margin: 0 0 0.875rem;
  padding: 0.625rem 0.75rem;
  border: 1px solid #0c4a6e;
  border-radius: 12px;
  background: #f0f9ff;
  color: #0c4a6e;
  font-family: Montserrat, sans-serif;
  text-align: left;
}

.sa-voice-candidates-q {
  margin: 0 0 0.5rem;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.3;
}

.sa-voice-candidates-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

/* 44 px is the floor, not the target: these are tapped in a cold room, often
   with a glove on, and the thing behind the tap is a sample move. Chips size
   to their text and wrap rather than scrolling sideways, because a candidate
   scrolled out of view is a candidate that gets guessed at instead. */
.sa-voice-chip {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 2px;

  min-height: 44px;
  padding: 0.375rem 0.75rem;

  border: 1px solid #0c4a6e;
  border-radius: 9999px;
  background: #ffffff;
  color: #0c4a6e;
  font-family: Montserrat, sans-serif;
  text-align: left;
  cursor: pointer;
}

.sa-voice-chip:focus-visible {
  outline: 3px solid #0c4a6e;
  outline-offset: 2px;
}

.sa-voice-chip-label {
  font-size: 15px;
  font-weight: 600;
  line-height: 1.2;
}

.sa-voice-chip-sub {
  font-size: 12px;
  font-weight: 400;
  line-height: 1.2;
  opacity: 0.75;
}

.sa-voice-candidates-help {
  margin: 0.5rem 0 0;
  font-size: 13px;
  line-height: 1.35;
}
</style>
